import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  AuthUser,
  getSession,
  refreshToken,
  signIn as cfSignIn,
  signOut as cfSignOut,
  isCloudflareAuthAvailable,
  captureSessionFromUrl,
  clearStoredSessionId,
} from '@/lib/cloudflare/auth-client';
import { useSyncStore } from '@/lib/store/sync.store';
import { clearEmailCache } from '@/lib/store/email-cache';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  status: AuthStatus;
  signIn: (returnUrl?: string) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Token refresh interval (45 minutes)
const REFRESH_INTERVAL = 45 * 60 * 1000;
// Token refresh buffer (5 minutes before expiry)
const REFRESH_BUFFER = 5 * 60 * 1000;

interface AuthProviderProps {
  children: React.ReactNode;
}

export function CFAuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef(false);
  const hasReportedAuthRef = useRef(false);
  const previousUserIdRef = useRef<string | null>(null);

  const clearAccountScopedState = useCallback(async () => {
    try {
      await clearEmailCache();
    } catch (error) {
      console.error('Failed to clear cached emails during account switch:', error);
    }

    useSyncStore.getState().resetForNewUser();
  }, []);

  const hydrateAccessToken = useCallback(async (): Promise<boolean> => {
    const result = await refreshToken();
    if (result) {
      setAccessToken(result.accessToken);
      setExpiresAt(result.expiresAt);
      return true;
    }

    return false;
  }, []);

  // Fetch session on mount
  const fetchSession = useCallback(async () => {
    if (!isCloudflareAuthAvailable()) {
      setStatus('unauthenticated');
      return;
    }

    try {
      const session = await getSession();

      if (session.user) {
        const nextUserId = session.user.id;

        if (previousUserIdRef.current && previousUserIdRef.current !== nextUserId) {
          await clearAccountScopedState();
        }

        setUser(session.user);
        setAccessToken(session.accessToken || null);
        
        // Setup Telemetry
        Sentry.setUser({ id: session.user.id, email: session.user.email });
        if (!hasReportedAuthRef.current) {
          Sentry.metrics.count("auth_success", 1);
          hasReportedAuthRef.current = true;
        }

        const storedUtms = localStorage.getItem('wb_utms');
        const utmProps = (() => { try { return storedUtms ? JSON.parse(storedUtms) : {}; } catch { return {}; } })();

        posthog.identify(session.user.id, { 
          email: session.user.email, 
          name: session.user.name,
          plan: session.user.currentPlan,
          ...utmProps
        });
        setExpiresAt(session.expiresAt || null);

        previousUserIdRef.current = nextUserId;
        await hydrateAccessToken();
        setStatus('authenticated');
      }

      if (!session.user) {
        if (previousUserIdRef.current) {
          await clearAccountScopedState();
          previousUserIdRef.current = null;
        }

        setUser(null);
        setAccessToken(null);
        
        // Clear Telemetry
        Sentry.setUser(null);
        posthog.reset();
        hasReportedAuthRef.current = false;
        setExpiresAt(null);
        setStatus('unauthenticated');
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      setStatus('unauthenticated');
    }
  }, [hydrateAccessToken, clearAccountScopedState]);

  // Refresh access token
  const refresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      const hydrated = await hydrateAccessToken();
      if (!hydrated) {
        // Refresh failed, re-fetch session
        await fetchSession();
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [fetchSession, hydrateAccessToken]);

  // Schedule token refresh
  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (!expiresAt) return;

    const now = Date.now();
    const expiresAtMs = expiresAt * 1000;
    const timeUntilRefresh = Math.max(0, expiresAtMs - now - REFRESH_BUFFER);

    if (timeUntilRefresh > 0) {
      refreshTimeoutRef.current = setTimeout(refresh, timeUntilRefresh);
    } else {
      // Token is expired or about to expire, refresh now
      refresh();
    }
  }, [expiresAt, refresh]);

  // Sign in handler
  const signIn = useCallback((returnUrl?: string) => {
    cfSignIn(returnUrl || window.location.pathname);
  }, []);

  // Sign out handler
  const signOut = useCallback(async () => {
    previousUserIdRef.current = null;
    await clearAccountScopedState();
    clearStoredSessionId();
    await cfSignOut();
    setUser(null);
    setAccessToken(null);
    setExpiresAt(null);
    setStatus('unauthenticated');
  }, [clearAccountScopedState]);

  // Initial session fetch
  useEffect(() => {
    // Check for session ID in URL (dev mode redirect)
    captureSessionFromUrl();

    // Then fetch session
    fetchSession();
  }, [fetchSession]);

  // Schedule refresh when expiresAt changes
  useEffect(() => {
    if (status === 'authenticated' && expiresAt) {
      scheduleRefresh();
    }

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [status, expiresAt, scheduleRefresh]);

  // Refresh on visibility change (tab focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && status === 'authenticated') {
        // Check if token needs refresh
        if (expiresAt) {
          const now = Math.floor(Date.now() / 1000);
          if (expiresAt - now < REFRESH_BUFFER / 1000) {
            refresh();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [status, expiresAt, refresh]);

  const value: AuthContextValue = {
    user,
    accessToken,
    status,
    signIn,
    signOut,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * Similar API to NextAuth's useSession
 */
export function useCFAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useCFAuth must be used within a CFAuthProvider');
  }

  return context;
}

/**
 * Compatibility layer - returns data in a similar format to NextAuth's useSession
 */
export function useSession() {
  const { user, accessToken, status } = useCFAuth();

  // Format similar to NextAuth's session
  const data = user
    ? {
        user: {
          id: user.id,
          email: user.email,
          name: user.name,
          image: user.image,
          cleanedEmailsCount: user.cleanedEmailsCount,
          lastCleanedAt: user.lastCleanedAt,
          lastGmailRefreshAt: user.lastGmailRefreshAt ?? null,
          driveConnected: user.driveConnected ?? false,
        },
        accessToken,
      }
    : null;

  return {
    data,
    status,
  };
}
